import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

const PERMISSIONS = {
  // Founder — full keys
  OWNER:             { users: true,  settings: true,  about: true,  homepage: true,  projects: true,  services: true,  media: true,  leads: true,  portal: true,  finance: true },
  // Day-to-day studio operations
  ADMIN_MANAGER:     { users: false, settings: false, about: true,  homepage: true,  projects: true,  services: true,  media: true,  leads: true,  portal: true,  finance: true },
  // Project execution support
  PROJECT_ASSISTANT: { users: false, settings: false, about: false, homepage: false, projects: true,  services: true,  media: true,  leads: false, portal: true,  finance: false },
  // Finance / payments / leads pipeline
  FINANCE:           { users: false, settings: false, about: false, homepage: false, projects: false, services: false, media: false, leads: true,  portal: false, finance: true  },
  // Legacy aliases (kept for older accounts in DB)
  ADMIN:             { users: false, settings: false, about: true,  homepage: true,  projects: true,  services: true,  media: true,  leads: true,  portal: true,  finance: true },
  EDITOR:            { users: false, settings: false, about: false, homepage: false, projects: true,  services: true,  media: true,  leads: true,  portal: true,  finance: false },
};

// Role → display label for the sidebar / profile / role pickers.
export const ROLE_LABELS = {
  OWNER: "Owner",
  ADMIN_MANAGER: "Admin Manager",
  PROJECT_ASSISTANT: "Project Assistant",
  FINANCE: "Finance",
  ADMIN: "Admin Manager",          // legacy alias
  EDITOR: "Project Assistant",     // legacy alias
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauthenticated, object = authenticated
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("gdl_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      return { ok: false, error: msg, status: e?.response?.status };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("gdl_token");
    setUser(false);
  };

  const role = (user && user.role) ? String(user.role).toUpperCase() : null;

  const value = useMemo(() => ({
    user,
    error,
    login,
    logout,
    refreshMe: fetchMe,
    role,
    isOwner: role === "OWNER",
    isAdmin: role === "OWNER" || role === "ADMIN" || role === "ADMIN_MANAGER",
    can: (feature) => (role ? !!PERMISSIONS[role]?.[feature] : false),
  }), [user, error, role, fetchMe]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
